<template>
	<div>
		<!-- Loading State -->
		<div v-if="firebaseUILoading" class="firebaseui-loading">
			<Spinner />
		</div>

		<!-- Error State -->
		<div v-else-if="firebaseUIError" class="firebaseui-error">
			<span class="message">{{ $t('WEBAPP_AUTH_LOAD_ERROR') }}</span>

			<BasicButton color="primary" @click="loadFirebaseUI">
				{{ $t('WEBAPP_ACTIONS_RETRY') }}
			</BasicButton>
		</div>

		<!-- FirebaseUI Container -->
		<div v-show="!firebaseUILoading && !firebaseUIError" id="firebaseui-auth-container"></div>

		<label
			v-if="mode === AccountModes.SIGN_UP && !firebaseUILoading && !firebaseUIError"
			class="notification-label"
		>
			<CheckboxInput v-model="acceptedEmailAgreement" />
			<span>{{ $t('WEBAPP_LOGINS_EMAIL_AGREE') }}</span>
		</label>
	</div>
</template>

<script setup lang="ts">
import type FirebaseUI from 'firebaseui';

import { onMounted, ref } from 'vue';

import { AccountModes } from '@/enums/login';
import { EmailAuthProvider, FacebookAuthProvider, GoogleAuthProvider, getAuth } from 'firebase/auth';

import { useLoginFlow } from '@/features/auth/composables/useLoginFlow';

import Spinner from '@/ui-components/Spinner.vue';
import BasicButton from '@/ui-components/BasicButton.vue';
import CheckboxInput from '@/ui-components/CheckboxInput.vue';

import { captureMessageForSentry } from '@/libraries/sentry';

const { mode, location, overrideSuccessUrl } = defineProps<{
	mode?: AccountModes | null;
	location?: string | null;
	overrideSuccessUrl?: string | null;
}>();

const emit = defineEmits<{ dismiss: [] }>();

const { t } = useI18n();

const { initLoginFlow } = useLoginFlow();

const acceptedEmailAgreement = ref(false);

onMounted(() => loadFirebaseUI());

const firebaseUILoading = ref<boolean>(true);
const firebaseUIError = ref<string | null>(null);

async function loadFirebaseUI() {
	try {
		firebaseUILoading.value = true;
		firebaseUIError.value = null;

		// Lazy load FirebaseUI
		const [firebaseuiModule] = await Promise.all([import('firebaseui'), import('firebaseui/dist/firebaseui.css')]);

		const firebaseui = firebaseuiModule.auth.AuthUI.getInstance() ?? new firebaseuiModule.auth.AuthUI(getAuth());
		initializeFirebaseUI(firebaseui);
	} catch (error) {
		console.log({ error });
		firebaseUIError.value = (error as Error)?.message ?? 'There was an error loading the Firebase UI.';

		captureMessageForSentry(
			'FirebaseUI lazy load failed',
			{ error: error as Error, where: '[SignInOrUpDialog.vue] loadFirebaseUI()' },
			'error'
		);
	} finally {
		firebaseUILoading.value = false;
	}
}

const AUTH_CONSENTS = 'AUTH_CONSENTS';
const signInFlow = 'popup';
const privacyPolicyUrl = 'https://www.justwatch.com/privacy-policy';
const tosUrl = 'https://www.justwatch.com/terms';

function initializeFirebaseUI(firebaseui: FirebaseUI.auth.AuthUI) {
	firebaseui.start('#firebaseui-auth-container', {
		signInFlow,
		tosUrl,
		privacyPolicyUrl,

		callbacks: {
			signInSuccessWithAuthResult: () => {
				try {
					sessionStorage.setItem(
						AUTH_CONSENTS,
						JSON.stringify({
							mode,
							location,
							acceptedPrivacyPolicy: true,
							acceptedEmailAgreement: acceptedEmailAgreement.value,
						})
					);
				} catch (error) {
					console.warn('[Auth] Failed to save auth consents to sessionStorage:', error);
				}

				initLoginFlow().then(() => {
					emit('dismiss');

					if (overrideSuccessUrl) window.location.assign(overrideSuccessUrl);
				});

				return false;
			},

			signInFailure: error => {
				captureMessageForSentry(
					'Firebase SignIn Failure',
					{
						error: { name: 'Firebase SignIn Failure', ...error },
						where: '[SignInOrUpDialog.vue] signInFailure()',
					},
					'error'
				);
			},
		},

		signInOptions: [
			{
				provider: 'apple.com',
				fullLabel: t('WEBAPP_LOGINS_CONTINUE_WITH_APPLE'),
			},
			{
				provider: FacebookAuthProvider.PROVIDER_ID,
				fullLabel: t('WEBAPP_LOGINS_CONTINUE_WITH_FACEBOOK'),
			},
			{
				provider: GoogleAuthProvider.PROVIDER_ID,
				fullLabel: t('WEBAPP_LOGINS_CONTINUE_WITH_GOOGLE'),
			},
			{
				provider: EmailAuthProvider.PROVIDER_ID,
				fullLabel: t('WEBAPP_LOGINS_CONTINUE_WITH_EMAIL'),
				requireDisplayName: false,
			},
		],
	});
}
</script>

<style scoped lang="scss">
.notification-label {
	display: flex;
	gap: 32px;
	align-items: center;
	padding-inline: 20px;
	text-align: left;
	margin-top: 24px;
	cursor: pointer;
	user-select: none;
}

.firebaseui-loading {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 40px;
	min-height: 200px;
}

.firebaseui-error {
	padding: 0;
	text-align: center;
	min-height: 0;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;

	&:not(:empty) {
		padding: 8px 0 !important;
		min-height: auto !important;
	}

	.message {
		color: #d32f2f;
		margin-bottom: 16px;
		font-size: 14px;
	}
}
</style>

<style lang="scss">
@import '~@/styles/variables.scss';

.firebaseui-container {
	.firebaseui-card-content {
		/* Apple Auth button */
		.firebaseui-idp-generic {
			background-color: white !important;

			.firebaseui-idp-icon {
				filter: invert(1);
			}
		}

		.firebaseui-idp-facebook {
			background-color: white !important;

			.firebaseui-idp-icon {
				filter: brightness(0.3) sepia(1) hue-rotate(157deg) saturate(13);
			}
		}

		.firebaseui-idp-password {
			background-color: $jw-color-gold--light !important;

			.firebaseui-idp-icon {
				filter: invert(1);
			}
		}
	}

	.firebaseui-card-footer {
		.firebaseui-tos {
			color: #d5d5d5 !important;

			.firebaseui-link {
				color: #78a6b8 !important;
			}
		}
	}
}
</style>
